import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Activity, HeartPulse, Scale, ScanEye } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const HOTSPOTS = [
  {
    id: "ecg",
    icon: Activity,
    pos: { top: "30%", left: "34%" },
    title: "Heart Checker (12-Lead ECG)",
    spec: "Reads your heart's electrical signals — the same test hospitals use.",
    compliance: "FDA Class II predicate component",
  },
  {
    id: "bp",
    icon: HeartPulse,
    pos: { top: "52%", left: "64%" },
    title: "Blood Pressure & Oxygen",
    spec: "Measures blood pressure and oxygen, just like the nurse's cuff — automatically.",
    compliance: "FDA Class II predicate component",
  },
  {
    id: "base",
    icon: Scale,
    pos: { top: "86%", left: "50%" },
    title: "Smart Base Plate",
    spec: "Just stand on it. It reads your weight, BMI and body fat in seconds.",
    compliance: "FDA Class II predicate component",
  },
  {
    id: "optical",
    icon: ScanEye,
    pos: { top: "12%", left: "50%" },
    title: "AI Camera Eyes",
    spec: "Smart cameras spot early warning signs — without ever touching you.",
    compliance: "FDA Class II predicate component",
  },
];

export const Hotspots = () => {
  const [active, setActive] = useState(null);
  const current = HOTSPOTS.find((h) => h.id === active);

  return (
    <section className="py-28 lg:py-40 px-6 bg-[#F9F9FB]" data-testid="hotspot-section">
      <div className="max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#808080] text-center"
        >
          Inside the Hub
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: EASE, delay: 0.1 }}
          className="font-display font-semibold tracking-tight text-center text-[#3A3A3C] text-3xl sm:text-4xl mt-4 leading-[1.15]"
        >
          A whole clinic, in one machine.
        </motion.h2>

        <div className="relative max-w-3xl mx-auto mt-16" data-testid="hotspot-stage">
          <motion.img
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.2, ease: EASE }}
            src="/media/hub-render.png"
            alt="SANTELQ Hub hardware render"
            className="w-full rounded-[2.5rem]"
            data-testid="hotspot-hub-image"
          />

          {HOTSPOTS.map((h) => (
            <button
              key={h.id}
              onClick={() => setActive(active === h.id ? null : h.id)}
              style={h.pos}
              aria-label={h.title}
              data-testid={`hotspot-${h.id}`}
              className={`hotspot-ring absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg transition-colors duration-300 ${
                active === h.id ? "bg-[#E84393]" : "bg-[#00F2FE]"
              }`}
            />
          ))}

          <AnimatePresence>
            {current && (
              <motion.aside
                key={current.id}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 60 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="mt-6 w-full sm:mt-0 sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2 sm:translate-x-[55%] sm:w-[320px] backdrop-blur-xl bg-white/90 border border-black/5 rounded-3xl p-6 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.25)] z-10"
                data-testid="hotspot-card"
              >
                <button
                  onClick={() => setActive(null)}
                  aria-label="Close spec card"
                  data-testid="hotspot-card-close"
                  className="absolute top-4 right-4 text-[#808080] hover:text-[#3A3A3C] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <current.icon className="w-6 h-6 text-[#E84393]" />
                <h4 className="font-display font-semibold text-[#3A3A3C] text-lg leading-snug mt-3 pr-6">
                  {current.title}
                </h4>
                <p className="text-sm text-[#474747] leading-relaxed mt-2">{current.spec}</p>
                <span className="inline-block mt-4 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#3A3A3C] bg-[#00F2FE]/15 border border-[#00F2FE]/40 rounded-full px-3 py-1.5">
                  {current.compliance}
                </span>
              </motion.aside>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
